import React, { useEffect, useRef } from 'react';
import { Close } from './Glyph';
import { createPortal } from 'react-dom';

/*
  One shell for every dialog in OnHand.

  Written 5 September 2026 for job 11, to rule 34, which the owner approved on 3
  September with Group E: **anything that opens over the screen and waits for an
  answer is a dialog, and every dialog is the same shell with different
  contents.**

  **There were eighteen of them and each one was its own shell**, hand built,
  which is how they drifted apart. Counted in the running app: eight on the
  counting screen, nine shared, and one on the movement screen that nobody had
  counted at all. What that cost, before this existed:

  - **A dialog had no maximum width**, so on a 1440 laptop every one of them was
    1,400 pixels across: a one line question with two buttons 670 wide each. Half
    fixed on 5 September with a maximum width; this is the rest of it.
  - **The footer scrolled away on a phone.** A dialog taller than the screen
    took its buttons off the bottom with it, so the act was a button nobody
    could press. The footer never scrolls now.
  - **Below 1024 a dialog was still a card in the middle of the screen**, when
    rule 34 says it rises from the bottom edge as a sheet.
  - **The kind of thing was said by painting the whole header**, blue or red,
    with the title in white on top of it. Rule 34 says the kind reads from a
    small tinted square, which is the same statement at a tenth of the ink.

  Not the scanning dialog, deliberately: the build order says it gets tokens and
  nothing more, because it is the one component expected to be thrown away when
  scanning moves to a real phone app.
*/

// The five kinds, and what each is for. The colour is the tint of the square in
// the head, never the whole header.
export const KINDS = {
  info:     { fg: 'var(--info)',       bg: 'var(--info-bg)' },
  danger:   { fg: 'var(--critical)',         bg: 'var(--critical-bg)' },
  approve:  { fg: 'var(--success)',        bg: 'var(--success-bg)' },
  question: { fg: 'var(--accent-ink)',   bg: 'var(--accent-bg)' },
  transfer: { fg: 'var(--info-ink)', bg: 'var(--info-bg)' },
};

// Rule 34's three widths, by what the dialog holds rather than by which screen
// it belongs to.
const SIZES = { question: 'dlg-narrow', form: '', list: 'dlg-wide' };

// The dialogs on screen, oldest first. See Escape, below.
const OPEN = [];

export default function Dialog({
  kind = 'info', icon, title, record, says, size = 'form',
  onClose, footer, children, wide, labelledBy,
}) {
  /*
    **Escape closes it, everywhere.** Six of the eighteen listened for Escape and
    twelve did not, so whether the key worked depended on which dialog you had
    open, which is exactly the sort of thing one shell exists to settle.

    **And only the one on top** (the audit after the rebrand, 19 September
    2026). Every open dialog listened, so with one opened from another (View
    only, then Ask for this sheet) one press of Escape closed both. Each dialog
    now takes its place on `OPEN` while it is on screen, and only the last one
    there answers. A dialog that cannot be closed (the terms) still takes its
    place, so Escape does not close what is underneath it either.
  */
  const me = useRef({});
  useEffect(() => {
    const mine = me.current;
    OPEN.push(mine);
    return () => { const at = OPEN.indexOf(mine); if (at >= 0) OPEN.splice(at, 1); };
  }, []);
  useEffect(() => {
    if (!onClose) return undefined;
    const onKey = e => {
      if (e.key === 'Escape' && OPEN[OPEN.length - 1] === me.current) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const tone = KINDS[kind] || KINDS.info;

  /*
    **It is drawn on the page itself, not where it was opened from. Found 7
    September 2026, and it had been broken since the shell was built.**

    Every card in OnHand carries a `backdrop-filter`, which is what gives the
    glass its blur. A browser rule nobody had run into says that an element with
    one of those becomes the frame that anything "fixed" inside it is measured
    against. The dialog shell is fixed to the corners of the screen, so a dialog
    opened from inside a card was being fixed to the corners of THE CARD.

    **On a laptop that looked almost right and was not**: the dialog centred
    itself in the card rather than on the screen. **Below 1024 it was a fault a
    person would meet.** The sheet rises from the bottom edge, so on Site
    Settings at 700 it rose from the bottom edge of a card that starts 787
    pixels down a page 1,900 pixels tall, and landed at 1,216: entirely off the
    screen. Pressing "Remove what is past 3 years" on a phone dimmed the page
    and appeared to do nothing at all.

    Measured in a real browser at 1440, 834, 700 and 390 before and after.
    Handing it to the page itself is the fix, and it holds wherever a dialog is
    opened from, which is the point of there being one shell.
  */
  const shell = (
    <div className="dlg-overlay" onClick={e => e.target === e.currentTarget && onClose && onClose()}>
      <div className={'dlg ' + (SIZES[size] || (wide ? 'dlg-wide' : ''))} role="dialog" aria-modal="true" aria-labelledby={labelledBy}>
        {/* The grab handle, which only the sheet shape shows. */}
        <div className="dlg-grab" aria-hidden="true" />

        <div className="dlg-head">
          <span className="dlg-mark" style={{ background: tone.bg, color: tone.fg }}>{icon}</span>
          <span className="dlg-said">
            <span className="dlg-title" id={labelledBy}>{title}</span>
            {record && <span className="dlg-record">{record}</span>}
            {/* Rule 34: it says once, under its title, what will happen when the
                act is pressed, and it is always in the same place. */}
            {says && <span className="dlg-says">{says}</span>}
          </span>
          {onClose && (
            <button type="button" className="dlg-x" onClick={onClose} aria-label="Close"><Close size={16} /></button>
          )}
        </div>

        <div className="dlg-body">{children}</div>

        {/* Rule 34: at most two, the way out on the left and the act on the
            right, and it never scrolls away. */}
        {footer && <div className="dlg-foot">{footer}</div>}
      </div>
    </div>
  );

  return typeof document === 'undefined' ? shell : createPortal(shell, document.body);
}
